// Email Template Service
// Database service layer for email template management with full CRUD operations

import {
  EmailTemplate,
  EmailTemplateCategory,
  EmailTemplateTag,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  TemplateSearchRequest,
  TemplateSearchResponse,
  TemplateAnalytics,
  BulkTemplateOperation,
  BulkOperationResult,
  TemplateValidationResult,
  TemplateVersionComparison,
  TemplateExportData,
  TemplateImportRequest,
  TemplateImportResult,
  EmailTemplateUsageHistory
} from '../types/emailTemplates';

// Mock database service - Replace with actual database implementation
class EmailTemplateService {
  private baseUrl = '/api/email-templates';
  private categoriesUrl = '/api/email-template-categories';
  private tagsUrl = '/api/email-template-tags';

  // ===== TEMPLATE CRUD OPERATIONS =====

  async createTemplate(request: CreateTemplateRequest, userId: string): Promise<EmailTemplate> {
    // Validate template
    const validation = await this.validateTemplate(request);
    if (!validation.isValid) {
      throw new Error(`Template validation failed: ${validation.errors.join(', ')}`);
    }

    // Generate template key if not provided
    const templateKey = request.templateKey || this.generateTemplateKey(request.name);

    // Create template data
    const templateData = {
      ...request,
      templateKey,
      id: this.generateId(),
      versionNumber: 1,
      isCurrentVersion: true,
      status: request.status || 'draft',
      usageCount: 0,
      hasEmbeddedImages: this.checkForEmbeddedImages(request.bodyHtml),
      embeddedImages: [],
      createdBy: userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // In real implementation, this would be a database call
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(templateData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create template: ${response.statusText}`);
    }

    return response.json();
  }

  async getTemplate(id: string, includeVersions = false): Promise<EmailTemplate | null> {
    const url = `${this.baseUrl}/${id}${includeVersions ? '?include_versions=true' : ''}`;

    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`Failed to fetch template: ${response.statusText}`);
    }

    return response.json();
  }

  async updateTemplate(id: string, request: UpdateTemplateRequest, userId: string): Promise<EmailTemplate> {
    // If creating new version, handle versioning
    if (request.createNewVersion) {
      return this.createNewVersion(id, request, userId);
    }

    // Validate updates
    const validation = await this.validateTemplateUpdate(id, request);
    if (!validation.isValid) {
      throw new Error(`Template validation failed: ${validation.errors.join(', ')}`);
    }

    const updateData = {
      ...request,
      updatedBy: userId,
      updatedAt: new Date().toISOString(),
      hasEmbeddedImages: request.bodyHtml ? this.checkForEmbeddedImages(request.bodyHtml) : undefined
    };

    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      throw new Error(`Failed to update template: ${response.statusText}`);
    }

    return response.json();
  }

  async deleteTemplate(id: string, userId: string, force = false): Promise<boolean> {
    // Check if template is system template
    const template = await this.getTemplate(id);
    if (template?.isSystemTemplate && !force) {
      throw new Error('Cannot delete system template without force flag');
    }

    // Check if template is in use
    const usage = await this.getTemplateUsage(id);
    if (usage.metrics.totalUsage > 0 && !force) {
      throw new Error('Cannot delete template that is in use. Use archive instead or force delete.');
    }

    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      headers: { 'X-User-Id': userId }
    });

    return response.ok;
  }

  async archiveTemplate(id: string, userId: string): Promise<EmailTemplate> {
    return this.updateTemplate(id, { status: 'archived' }, userId);
  }

  async activateTemplate(id: string, userId: string): Promise<EmailTemplate> {
    return this.updateTemplate(id, { status: 'active' }, userId);
  }

  // ===== VERSION CONTROL =====

  async createNewVersion(templateId: string, changes: UpdateTemplateRequest, userId: string): Promise<EmailTemplate> {
    const currentTemplate = await this.getTemplate(templateId);
    if (!currentTemplate) {
      throw new Error('Template not found');
    }

    // For now, use standard update with versioning flag
    const updateData = {
      ...changes,
      createNewVersion: true
    };

    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create new version: ${response.statusText}`);
    }

    return response.json();
  }

  async getTemplateVersions(templateKey: string): Promise<EmailTemplate[]> {
    const response = await fetch(`${this.baseUrl}/versions/${templateKey}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch template versions: ${response.statusText}`);
    }

    return response.json();
  }

  async compareVersions(templateId1: string, templateId2: string): Promise<TemplateVersionComparison> {
    const response = await fetch(`${this.baseUrl}/compare/${templateId1}/${templateId2}`);

    if (!response.ok) {
      throw new Error(`Failed to compare versions: ${response.statusText}`);
    }

    return response.json();
  }

  async revertToVersion(currentId: string, targetVersionId: string, userId: string): Promise<EmailTemplate> {
    const targetVersion = await this.getTemplate(targetVersionId);
    if (!targetVersion) {
      throw new Error('Target version not found');
    }

    // Create new version based on target version
    return this.createNewVersion(currentId, {
      name: targetVersion.name,
      description: targetVersion.description,
      subject: targetVersion.subject,
      bodyHtml: targetVersion.bodyHtml,
      bodyText: targetVersion.bodyText,
      variables: targetVersion.variables,
      defaultValues: targetVersion.defaultValues
    }, userId);
  }

  // ===== SEARCH AND FILTERING =====

  async searchTemplates(request: TemplateSearchRequest): Promise<TemplateSearchResponse> {
    const params = new URLSearchParams();

    // Build query parameters
    if (request.query) params.append('q', request.query);
    if (request.department) params.append('department', request.department);
    if (request.categoryId) params.append('category', request.categoryId);
    if (request.tagIds) params.append('tags', request.tagIds.join(','));
    if (request.status) params.append('status', request.status.join(','));
    if (request.createdBy) params.append('created_by', request.createdBy);
    if (request.dateRange) {
      params.append('date_start', request.dateRange.start);
      params.append('date_end', request.dateRange.end);
    }
    if (request.includeArchived) params.append('include_archived', 'true');
    if (request.onlyCurrentVersions) params.append('current_versions_only', 'true');
    if (request.sortBy) params.append('sort', request.sortBy);
    if (request.sortOrder) params.append('order', request.sortOrder);
    if (request.limit) params.append('limit', request.limit.toString());
    if (request.offset) params.append('offset', request.offset.toString());

    const response = await fetch(`${this.baseUrl}/search?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`Search failed: ${response.statusText}`);
    }

    return response.json();
  }

  async getTemplatesByCategory(categoryId: string): Promise<EmailTemplate[]> {
    return this.searchTemplates({ categoryId, onlyCurrentVersions: true }).then(r => r.templates);
  }

  async getTemplatesByTag(tagId: string): Promise<EmailTemplate[]> {
    return this.searchTemplates({ tagIds: [tagId], onlyCurrentVersions: true }).then(r => r.templates);
  }

  async getTemplatesByDepartment(department: string): Promise<EmailTemplate[]> {
    return this.searchTemplates({
      department: department as any,
      onlyCurrentVersions: true,
      status: ['active']
    }).then(r => r.templates);
  }

  // ===== CATEGORIES =====

  async createCategory(name: string, description: string, department: string, userId: string): Promise<EmailTemplateCategory> {
    const categoryData = {
      name,
      description,
      department,
      colorHex: '#3B82F6', // Default blue
      icon: 'Mail',
      isSystemCategory: false,
      displayOrder: 999,
      createdBy: userId
    };

    const response = await fetch(this.categoriesUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(categoryData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create category: ${response.statusText}`);
    }

    return response.json();
  }

  async getCategories(department?: string): Promise<EmailTemplateCategory[]> {
    const url = department
      ? `${this.categoriesUrl}?department=${department}`
      : this.categoriesUrl;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.statusText}`);
    }

    return response.json();
  }

  async updateCategory(id: string, updates: Partial<EmailTemplateCategory>, userId: string): Promise<EmailTemplateCategory> {
    const response = await fetch(`${this.categoriesUrl}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...updates, updatedBy: userId })
    });

    if (!response.ok) {
      throw new Error(`Failed to update category: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== TAGS =====

  async createTag(name: string, description: string, colorHex: string, userId: string): Promise<EmailTemplateTag> {
    const tagData = {
      name,
      description,
      colorHex,
      createdBy: userId
    };

    const response = await fetch(this.tagsUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tagData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create tag: ${response.statusText}`);
    }

    return response.json();
  }

  async getTags(): Promise<EmailTemplateTag[]> {
    const response = await fetch(this.tagsUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch tags: ${response.statusText}`);
    }

    return response.json();
  }

  async assignTagsToTemplate(templateId: string, tagIds: string[], userId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${templateId}/tags`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tagIds, assignedBy: userId })
    });

    if (!response.ok) {
      throw new Error(`Failed to assign tags: ${response.statusText}`);
    }
  }

  // ===== ANALYTICS AND USAGE =====

  async getTemplateUsage(templateId: string): Promise<TemplateAnalytics> {
    const response = await fetch(`${this.baseUrl}/${templateId}/analytics`);

    if (!response.ok) {
      throw new Error(`Failed to fetch template analytics: ${response.statusText}`);
    }

    return response.json();
  }

  async recordTemplateUsage(templateId: string, clientId: string, userId: string, variablesUsed: Record<string, string>): Promise<void> {
    const usageData = {
      templateId,
      clientId,
      usedBy: userId,
      variablesUsed,
      success: true
    };

    const response = await fetch(`${this.baseUrl}/${templateId}/usage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(usageData)
    });

    if (!response.ok) {
      throw new Error(`Failed to record usage: ${response.statusText}`);
    }
  }

  async getUsageHistory(templateId: string, limit = 50): Promise<EmailTemplateUsageHistory[]> {
    const response = await fetch(`${this.baseUrl}/${templateId}/usage?limit=${limit}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch usage history: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== BULK OPERATIONS =====

  async bulkOperation(operation: BulkTemplateOperation, userId: string): Promise<BulkOperationResult> {
    const response = await fetch(`${this.baseUrl}/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...operation, performedBy: userId })
    });

    if (!response.ok) {
      throw new Error(`Bulk operation failed: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== VALIDATION =====

  async validateTemplate(template: CreateTemplateRequest): Promise<TemplateValidationResult> {
    const response = await fetch(`${this.baseUrl}/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(template)
    });

    if (!response.ok) {
      throw new Error(`Validation failed: ${response.statusText}`);
    }

    return response.json();
  }

  async validateTemplateUpdate(id: string, updates: UpdateTemplateRequest): Promise<TemplateValidationResult> {
    const response = await fetch(`${this.baseUrl}/${id}/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error(`Validation failed: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== IMPORT/EXPORT =====

  async exportTemplate(id: string, includeImages = true): Promise<TemplateExportData> {
    const url = `${this.baseUrl}/${id}/export${includeImages ? '?include_images=true' : ''}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    return response.json();
  }

  async importTemplate(importRequest: TemplateImportRequest, userId: string): Promise<TemplateImportResult> {
    const response = await fetch(`${this.baseUrl}/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...importRequest, importedBy: userId })
    });

    if (!response.ok) {
      throw new Error(`Import failed: ${response.statusText}`);
    }

    return response.json();
  }

  // ===== UTILITY METHODS =====

  private generateId(): string {
    return `tpl_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateTemplateKey(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 100);
  }

  private checkForEmbeddedImages(htmlContent: string): boolean {
    const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
    return imgRegex.test(htmlContent);
  }

  async previewTemplate(templateId: string, variableValues: Record<string, string>): Promise<{ subject: string; bodyHtml: string }> {
    const response = await fetch(`${this.baseUrl}/${templateId}/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variables: variableValues })
    });

    if (!response.ok) {
      throw new Error(`Preview generation failed: ${response.statusText}`);
    }

    return response.json();
  }

  async duplicateTemplate(id: string, newName: string, userId: string): Promise<EmailTemplate> {
    const original = await this.getTemplate(id);
    if (!original) {
      throw new Error('Template not found');
    }

    return this.createTemplate({
      name: newName,
      description: `Copy of ${original.name}`,
      subject: original.subject,
      bodyHtml: original.bodyHtml,
      bodyText: original.bodyText,
      department: original.department,
      variables: original.variables,
      defaultValues: original.defaultValues,
      status: 'draft'
    }, userId);
  }
}

// Export singleton instance
export const emailTemplateService = new EmailTemplateService();
export default emailTemplateService;